/**
 * Design Tokens - Enterprise grade.
 *
 * Nguyên tắc:
 *  - Neutral palette chính, accent rất tinh tế.
 *  - Radius tối đa 6px (không 12-16).
 *  - Shadow gần như không có; chỉ dùng cho overlay/modal/dropdown.
 *  - Spacing scale rõ ràng, đồng bộ toàn hệ thống.
 *  - Typography hierarchy bằng size + weight, không màu mè.
 */

// ============ NEUTRAL PALETTE (slate) ============
export const neutral = {
  0: "#FFFFFF",
  25: "#FCFCFD",
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
};

// ============ BRAND / ACCENT ============
// Deep navy as the only brand colour. Accent xanh tối khi cần CTA.
export const brand = {
  primary: "#0F2A47",       // Deep enterprise navy
  primaryHover: "#143256",
  primarySubtle: "#F1F4F9", // Light tint
  accent: "#1D4ED8",        // Reserved for primary CTAs only
};

// ============ SHINKO BRAND (bộ nhận diện chính thức của khách) ============
// Đúng 2 màu theo brand guideline. `cement` chính là màu tagline trong file logo.
// LƯU Ý: cam chỉ dành cho nhận diện thương hiệu (logo, favicon, theme-color,
// hover link brand). Không đưa vào nút primary — nó trùng vùng màu với
// `semantic.danger`, sẽ làm nút "Lưu" và nút "Xoá" cùng một họ đỏ.
export const shinko = {
  orange: "#EA4125",  // Đỏ cam SHINKO — rgb(233, 67, 37)
  cement: "#5C6F72",  // Xám xi măng — rgb(92, 111, 114)
};

// ============ SIDEBAR (dark navy shell — tham khảo CRM Conek) ============
// Dùng cho khung điều hướng bên trái (luôn navy, tách khỏi theme content).
export const sidebar = {
  bg: "#0E1A3A",
  bgSub: "#0B142E",   // nền submenu
  hover: "#1A2950",
  // Item đang chọn. Cố ý dùng xanh CTA (`brand.accent`) chứ không phải một sắc
  // navy đậm hơn: nền cũ #22346A chỉ hơn `hover` một chút nên nhìn vào cột menu
  // không đoán được đang đứng ở đâu.
  active: "#1D4ED8",
  border: "#1C2B52",
  label: "#7F8DB4",   // nhãn nhóm / chú thích
  text: "#C6D0E6",    // chữ item mặc định
};

// ============ GOLD ACCENT (brand mark) ============
export const gold = {
  base: "#EAA31C",
  soft: "#FBEECB",
};

// ============ SURFACE (canvas / divider — CRM) ============
export const surface = {
  canvas: "#F5F7FB",  // nền vùng nội dung
  line: "#E9EDF4",    // đường kẻ mảnh
};

// ============ SEMANTIC (đã tone xuống) ============
export const semantic = {
  success: "#0F766E",       // Teal-700
  successSubtle: "#F0FDFA",
  warning: "#B45309",       // Amber-700 (less yellow)
  warningSubtle: "#FFFBEB",
  danger: "#B91C1C",        // Red-700
  dangerSubtle: "#FEF2F2",
  info: "#1E40AF",          // Blue-800
  infoSubtle: "#EFF6FF",
};

// ============ RADIUS ============
export const radius = {
  none: 0,
  sm: 2,
  base: 4,
  md: 6,
  lg: 6,       // Cap ở 6px - không lớn hơn
  pill: 999,
};

// ============ SPACING SCALE ============
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
};

// ============ TYPOGRAPHY ============
export const typography = {
  family:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  // Sizes
  size: {
    xs: 11,
    sm: 12,
    base: 13,
    md: 14,
    lg: 16,
    xl: 18,
    "2xl": 22,
    "3xl": 28,
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  // Line height
  leading: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.65,
  },
};

// ============ SHADOWS (rất nhẹ) ============
export const shadow = {
  none: "none",
  // Subtle: card / panel
  xs: "0 1px 2px rgba(15, 23, 42, 0.04)",
  // Dropdown / popover
  sm: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
  // Modal / dialog
  md: "0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)",
  // Hover lift
  hover: "0 2px 6px rgba(15, 23, 42, 0.06)",
};

// ============ BORDERS ============
export const border = {
  hairline: `1px solid ${neutral[200]}`,
  base: `1px solid ${neutral[300]}`,
  strong: `1px solid ${neutral[400]}`,
  focus: `1px solid ${brand.accent}`,
  divider: `1px solid ${neutral[100]}`,
};

// ============ LAYOUT ============
export const layout = {
  pagePadding: spacing[6],     // 24
  sectionGap: spacing[6],      // 24
  cardPadding: spacing[6],     // 24
  fieldGap: spacing[4],        // 16
  contentMaxWidth: 1440,
};

// ============ Z-INDEX ============
export const z = {
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
};

const tokens = {
  neutral,
  brand,
  sidebar,
  gold,
  surface,
  semantic,
  radius,
  spacing,
  typography,
  shadow,
  border,
  layout,
  z,
};

export default tokens;
